import { useState } from "react";
import {
  useStripe,
  useElements,
  PaymentElement,
  Elements,
} from "@stripe/react-stripe-js";
import { Appearance, loadStripe, StripeElementsOptions } from "@stripe/stripe-js";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { LoadingButton } from "@mui/lab";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import axios from "axios";
import { slackHook } from "src/utils/snug/slackHooks";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "");

const PLANS = {
  monthly: { label: "Starter (Monthly)", amount: 3000, display: "$3,000" },
  annual: { label: "Starter (Annual)", amount: 6000, display: "$6,000" },
};

const appearance: Appearance = {
  variables: {
    fontFamily: '"Gill Sans", sans-serif',
    fontLineHeight: "1.5",
    borderRadius: "10px",
    colorBackground: "#F6F8FA",
    colorPrimaryText: "#262626",
  },
  rules: {
    ".Block": { backgroundColor: "var(--colorBackground)", boxShadow: "none", padding: "12px" },
    ".Input": { padding: "12px" },
    ".Tab": { padding: "10px 12px 8px 12px", border: "none" },
    ".Tab--selected, .Tab--selected:focus, .Tab--selected:hover": {
      border: "none",
      backgroundColor: "#fff",
      boxShadow: "0 0 0 1.5px var(--colorPrimaryText), 0px 1px 1px rgba(0,0,0,0.03)",
    },
    ".Label": { fontWeight: "500" },
  },
};

interface FormData {
  name: string;
  company: string;
  email: string;
  plan: "monthly" | "annual";
  terms: boolean;
}

function PaymentForm({ formData, onSuccess }: { formData: FormData; onSuccess: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setLoading(true);
    setError("");

    const { paymentIntent, error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });

    if (stripeError) {
      setError(stripeError.message || "Payment failed. Please try again.");
      setLoading(false);
      return;
    }

    if (paymentIntent?.status === "succeeded" || paymentIntent?.status === "processing") {
      const plan = PLANS[formData.plan];
      slackHook(
        `🎉 STARTER PAYMENT ${paymentIntent.status.toUpperCase()}\n` +
        `Name: ${formData.name}\n` +
        `Company: ${formData.company}\n` +
        `Email: ${formData.email}\n` +
        `Plan: ${plan.label} (${plan.display})`
      );
      onSuccess();
    } else {
      setError("Payment could not be completed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <PaymentElement />
      {error && <Typography color="error" variant="body2">{error}</Typography>}
      <LoadingButton
        variant="contained"
        size="large"
        loading={loading}
        onClick={handleSubmit}
        disabled={!stripe}
        startIcon={<LockRoundedIcon />}
        sx={{ borderRadius: 1 }}
      >
        Pay {PLANS[formData.plan].display} and get started
      </LoadingButton>
    </Stack>
  );
}

const isGetSnugDomain = window.location.hostname.endsWith("getsnug.com");

export default function StarterPayPage() {
  const [step, setStep] = useState<"form" | "payment" | "success">("form");
  const [clientSecret, setClientSecret] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState<FormData>({
    name: "",
    company: "",
    email: "",
    plan: "monthly",
    terms: false,
  });

  const apiBase = process.env.REACT_APP_SNUG_API_URL || "";

  if (!isGetSnugDomain) return null;

  const handleContinue = async () => {
    setError("");

    if (!formData.name.trim() || !formData.company.trim() || !formData.email.trim()) {
      setError("Please fill in all fields.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!formData.terms) {
      setError("You must agree to the Terms and Conditions.");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${apiBase}/v3/starter-pay/payment-intent/`, {
        plan: formData.plan,
        name: formData.name,
        company: formData.company,
        email: formData.email,
      });
      setClientSecret(data.client_secret);
      setStep("payment");
    } catch (e: any) {
      setError(e?.response?.data?.error || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (step === "success") {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#fafafa" }}>
        <Card sx={{ maxWidth: 480, width: "100%", mx: 2, borderRadius: 2 }}>
          <CardContent sx={{ p: 4, textAlign: "center" }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
            <Typography variant="h5" gutterBottom>Payment successful!</Typography>
            <Typography variant="body1" color="text.secondary">
              You will receive instructions from us shortly at <strong>{formData.email}</strong>.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fafafa" }}>
      <Box sx={{ px: 4, py: 3 }}>
        <img
          src="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/snug_logo.png"
          alt="Snug"
          style={{ height: 32 }}
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", pb: 6 }}>
      <Card sx={{ maxWidth: 520, width: "100%", mx: 2, borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h5" gutterBottom>
                {step === "payment" ? "Complete your payment" : "Choose your Snug plan"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {step === "payment"
                  ? `${PLANS[formData.plan].label} — ${PLANS[formData.plan].display}`
                  : "Both options include full platform access, white-label setup, and onboarding support. The annual plan waives the $3,000 onboarding fee — saving you money upfront and locking in your rate for the year."}
              </Typography>
            </Box>

            {step === "form" && (
              <>
                <ToggleButtonGroup
                  exclusive
                  value={formData.plan}
                  onChange={(_, val) => val && setFormData(f => ({ ...f, plan: val }))}
                  fullWidth
                >
                  <ToggleButton value="monthly" sx={{ borderRadius: 1, py: 1.5 }}>
                    <Stack alignItems="center">
                      <Typography variant="subtitle2">Starter (Monthly)</Typography>
                      <Typography variant="h6">$3,000</Typography>
                    </Stack>
                  </ToggleButton>
                  <ToggleButton value="annual" sx={{ borderRadius: 1, py: 1.5 }}>
                    <Stack alignItems="center">
                      <Typography variant="subtitle2">Starter (Annual)</Typography>
                      <Typography variant="h6">$6,000</Typography>
                    </Stack>
                  </ToggleButton>
                </ToggleButtonGroup>

                <Box sx={{ bgcolor: "#f0f4f8", borderRadius: 2, p: 2.5 }}>
                  {formData.plan === "monthly" ? (
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" fontWeight={600}>Starter — Monthly</Typography>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        $3,000 onboarding, then $500/month + usage
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Your onboarding fee covers your white-label setup, implementation support, and the first three months of launch. Monthly billing begins after onboarding is complete, so you have time to ramp up.
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Best for:</strong> Teams that want to get started before committing to a full year.
                      </Typography>
                    </Stack>
                  ) : (
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" fontWeight={600}>Starter — Annual</Typography>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        $6,000/year + usage, onboarding included
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pay once for the full year and your onboarding fee is waived. You get everything in the monthly plan at a lower total cost, with no monthly billing to manage.
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Best for:</strong> Teams ready to commit and looking to maximize value from day one.
                      </Typography>
                    </Stack>
                  )}
                </Box>

                <TextField
                  label="Full Name"
                  fullWidth
                  value={formData.name}
                  onChange={e => setFormData(f => ({ ...f, name: e.target.value }))}
                />
                <TextField
                  label="Company Name"
                  fullWidth
                  value={formData.company}
                  onChange={e => setFormData(f => ({ ...f, company: e.target.value }))}
                />
                <TextField
                  label="Work Email Address"
                  fullWidth
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData(f => ({ ...f, email: e.target.value }))}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.terms}
                      onChange={e => setFormData(f => ({ ...f, terms: e.target.checked }))}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I have read and agree to the{" "}
                      <a
                        href="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/Professional+Terms+of+Service.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms and Conditions
                      </a>
                    </Typography>
                  }
                />

                {error && <Typography color="error" variant="body2">{error}</Typography>}

                <LoadingButton
                  variant="contained"
                  size="large"
                  loading={loading}
                  onClick={handleContinue}
                  sx={{ borderRadius: 1 }}
                >
                  Continue to payment
                </LoadingButton>
              </>
            )}

            {step === "payment" && clientSecret && (
              <Elements
                stripe={stripePromise}
                options={{ clientSecret, appearance, loader: "auto" } as StripeElementsOptions}
              >
                <PaymentForm formData={formData} onSuccess={() => setStep("success")} />
              </Elements>
            )}
          </Stack>
        </CardContent>
      </Card>
      </Box>
    </Box>
  );
}
